import type { Fetcher } from "../fetcher";
import type { ApiCollectionResponse } from "../types/api";
import type {
  ChatInboxResourceType,
  ChatInboxThreadDTO,
  ListInboxThreadsParams,
} from "./types";

/**
 * Repository for the global chat inbox. Threads aggregate the
 * quotes + orders the user is involved in; the mark-read endpoint
 * resets the unread cutoff for one thread.
 */
export function createChatInboxRepository(fetcher: Fetcher) {
  return {
    listThreads: async (
      params: ListInboxThreadsParams = {},
    ): Promise<{
      items: ChatInboxThreadDTO[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }> => {
      const res = await fetcher<ApiCollectionResponse<ChatInboxThreadDTO>>(
        "/chat-inbox/threads",
        { params: params as Record<string, unknown> },
      );
      const payload = res.data ?? ({} as Partial<{
        items: ChatInboxThreadDTO[];
        pagination: { page: number; limit: number; total: number };
      }>);
      const pagination = payload.pagination ?? ({} as { page?: number; limit?: number; total?: number });
      const limit = pagination.limit ?? params.limit ?? 20;
      const total = pagination.total ?? 0;
      return {
        items: payload.items ?? [],
        pagination: {
          page: pagination.page ?? params.page ?? 1,
          limit,
          total,
          totalPages: Math.max(1, Math.ceil(total / Math.max(1, limit))),
        },
      };
    },

    markThreadRead: async (
      resourceType: ChatInboxResourceType,
      resourceId: string,
    ): Promise<void> => {
      await fetcher(`/chat-inbox/${resourceType}/${resourceId}/read`, {
        method: "POST",
      });
    },
  };
}
